import {
    SmartCallModalHeader,
    SmartCallProfile,
    SmartCallTab,
    SmartCallTabs,
    useSmartCallOutletContext,
} from './CloudConnectorSmartCall.tsx';
import { OperatorRoutedModalContent } from '../layout/components/OperatorRoutedModalContent.tsx';
import {
    FormContentLayout,
    FormLeftColumn,
    FormRightColumn,
} from '../layout/components/form/FormLayout.tsx';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { Alert, Box, FormControlLabel, Switch } from '@mui/material';
import { type JSX, useMemo, useState } from 'react';
import {
    RequestMethod,
    RequestStatus,
    useServerFetch,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import {
    WeekDay,
    weekdayText,
} from 'dg-web-shared/common/components/translations/WeekdayTexts.ts';
import { OperatorTypo } from '../ui/OperatorTypo.ts';
import { Edit } from '@mui/icons-material';
import { Clickable } from 'dg-web-shared/ui/Clickable.tsx';
import { useEasyForm } from 'dg-web-shared/common/utils/FormHooksUtils.tsx';
import {
    Control,
    UseFormSetValue,
    UseFormWatch,
    useWatch,
} from 'react-hook-form';
import { ValidationData } from 'dg-web-shared/lib/forms/FormValidationHelpers.tsx';
import { GenericFormSubmitError } from '../parkingabo-users/ParkingaboUsersUtils.tsx';
import { HeaderActionCloseButtons } from '../layout/components/HeaderActionCloseButtons.tsx';
import { HeaderCancelSaveButtons } from '../layout/components/HeaderCancelSaveButtons.tsx';
import { Typo } from 'dg-web-shared/ui/typo.ts';
import { css } from '@emotion/css';
import {
    hasFullDayConfigured,
    SmartCallTimeWindowConfigDisplay,
    SmartCallTimeWindowConfigEditDialog,
    SmartCallTimeWindowConfig,
} from './CloudConnectorSmartCallTimeWindowConfig.tsx';

export function CloudConnectorSmartCallWeeklyPlanTab() {
    const {
        zoneId,
        onClose,
        navigateWithinModal,
        profilesState,
        onDirtyStateChange,
        refetchProfiles,
    } = useSmartCallOutletContext();

    const [weeklyConfigState, refetchWeeklyConfig] = useServerFetch<
        SmartCallWeeklyConfig,
        { zoneId: string }
    >(
        ({ zoneId }) => ({
            url: `/ui-api/operator-account/cloud-connector/smart-call/zone/${zoneId}/weekly-config`,
        }),
        { zoneId: zoneId },
    );

    return (
        <OperatorAsyncLoadedSection
            requestState={weeklyConfigState}
            render={weeklyConfig => (
                <CloudConnectorSmartCallWeeklyPlanContent
                    weeklyConfig={weeklyConfig}
                    refetchWeeklyConfig={refetchWeeklyConfig}
                    zoneId={zoneId}
                    onDirtyStateChange={onDirtyStateChange}
                    onClose={onClose}
                    profilesState={profilesState}
                    navigateWithinModal={navigateWithinModal}
                    refetchProfiles={refetchProfiles}
                />
            )}
        />
    );
}

function CloudConnectorSmartCallWeeklyPlanContent({
    weeklyConfig,
    refetchWeeklyConfig,
    zoneId,
    onDirtyStateChange,
    onClose,
    profilesState,
    navigateWithinModal,
    refetchProfiles,
}: {
    weeklyConfig: SmartCallWeeklyConfig;
    refetchWeeklyConfig: () => void;
    zoneId: string;
    onDirtyStateChange: (shouldConfirm: boolean) => void;
    onClose: () => void;
    profilesState: ReturnType<
        typeof useSmartCallOutletContext
    >['profilesState'];
    navigateWithinModal: ReturnType<
        typeof useSmartCallOutletContext
    >['navigateWithinModal'];
    refetchProfiles: () => void;
}) {
    const [editState, editWeeklyPlan] = useServerWrite<
        EditPayload & { zoneId: string },
        object,
        ValidationData
    >(() => ({
        url: `/ui-api/operator-account/cloud-connector/smart-call/zone/${zoneId}/weekly-config`,
        method: RequestMethod.PUT,
    }));

    useServerSuccessEffect(editState, () => {
        refetchWeeklyConfig();
        refetchProfiles();
    });

    const { language } = useLanguage();

    const {
        formInfo: { control, watch, setValue, handleSubmit },
        formState: { isDirty },
        genericSubmitError,
    } = useEasyForm(onDirtyStateChange, editState, language, {
        defaultValues: {
            [FormFields.config]: weeklyConfig,
        },
    });

    return (
        <OperatorRoutedModalContent
            header={
                <SmartCallModalHeader
                    editMode={isDirty}
                    buttons={
                        isDirty ? (
                            <HeaderCancelSaveButtons
                                onCancel={() => {
                                    refetchWeeklyConfig();
                                }}
                                onSave={handleSubmit((payload: EditPayload) =>
                                    editWeeklyPlan({
                                        config: payload.config,
                                        zoneId: zoneId,
                                    }),
                                )}
                                loading={
                                    editState.status === RequestStatus.PENDING
                                }
                            />
                        ) : (
                            <HeaderActionCloseButtons onClose={onClose} />
                        )
                    }
                />
            }
            bar={
                <SmartCallTabs
                    smartCallTab={SmartCallTab.WEEKLY_PLAN}
                    navigateWithinModal={navigateWithinModal}
                />
            }
            style={{ padding: '0px 0px' }}
            body={
                <OperatorAsyncLoadedSection
                    requestState={profilesState}
                    render={(profiles: SmartCallProfile[]) => (
                        <>
                            {genericSubmitError != null && (
                                <GenericFormSubmitError
                                    error={genericSubmitError}
                                    submitState={editState}
                                />
                            )}
                            <SmartCallWeeklyPlanForm
                                profiles={profiles}
                                weeklyConfig={weeklyConfig}
                                control={control}
                                watch={watch}
                                setValue={setValue}
                            />
                        </>
                    )}
                />
            }
        />
    );
}

enum FormFields {
    config = 'config',
}

type EditPayload = {
    [FormFields.config]: SmartCallWeeklyConfig;
};

enum WeeklyConfigFields {
    monday = 'monday',
    tuesday = 'tuesday',
    wednesday = 'wednesday',
    thursday = 'thursday',
    friday = 'friday',
    saturday = 'saturday',
    sunday = 'sunday',
}

export type SmartCallWeeklyConfig = {
    [WeeklyConfigFields.monday]: SmartCallTimeWindowConfig[];
    [WeeklyConfigFields.tuesday]: SmartCallTimeWindowConfig[];
    [WeeklyConfigFields.wednesday]: SmartCallTimeWindowConfig[];
    [WeeklyConfigFields.thursday]: SmartCallTimeWindowConfig[];
    [WeeklyConfigFields.friday]: SmartCallTimeWindowConfig[];
    [WeeklyConfigFields.saturday]: SmartCallTimeWindowConfig[];
    [WeeklyConfigFields.sunday]: SmartCallTimeWindowConfig[];
};

function SmartCallWeeklyPlanForm({
    profiles,
    weeklyConfig,
    control,
    watch,
    setValue,
}: {
    profiles: SmartCallProfile[];
    weeklyConfig: SmartCallWeeklyConfig;
    control: Control<EditPayload, object>;
    watch: UseFormWatch<EditPayload>;
    setValue: UseFormSetValue<EditPayload>;
}) {
    const config = useWatch({ control, name: FormFields.config });
    const monday: SmartCallTimeWindowConfig[] = useWatch({
        control,
        name: `${FormFields.config}.${WeeklyConfigFields.monday}`,
    });
    const saturday: SmartCallTimeWindowConfig[] = useWatch({
        control,
        name: `${FormFields.config}.${WeeklyConfigFields.saturday}`,
    });

    const [allDaysSame, setAllDaysSame] = useState(() =>
        areAllDaysEqual(Object.values(weeklyConfig)),
    );
    const [allWeekdaysSame, setAllWeekdaysSame] = useState(() =>
        areAllDaysEqual([
            weeklyConfig.monday,
            weeklyConfig.tuesday,
            weeklyConfig.wednesday,
            weeklyConfig.thursday,
            weeklyConfig.friday,
        ]),
    );
    const [allWeekendSame, setAllWeekendSame] = useState(() =>
        areAllDaysEqual([weeklyConfig.saturday, weeklyConfig.sunday]),
    );
    const [openDailyConfigEditModal, setOpenDailyConfigEditModal] =
        useState<DailyConfigField | null>(null);

    const dailyConfigurationFields: DailyConfigField[] = useMemo(() => {
        if (allDaysSame) {
            return [
                {
                    label: (
                        <Localized
                            de="Montag - Sonntag"
                            fr="Lundi - Dimanche"
                            it="Lunedì - Domenica"
                            en="Monday - Sunday"
                        />
                    ),
                    values: monday,
                    day: 'allDays' as WeeklyConfigDayKeys,
                },
            ];
        }

        const daysConfigFields: Array<{
            label: JSX.Element;
            values: SmartCallTimeWindowConfig[];
            day: WeeklyConfigDayKeys;
        }> = [];

        if (allWeekdaysSame) {
            daysConfigFields.push({
                day: 'weekdays',
                label: (
                    <Localized
                        de="Montag - Freitag"
                        fr="Lundi - Vendredi"
                        it="Lunedì - Venerdì"
                        en="Monday - Friday"
                    />
                ),
                values: monday,
            });
        } else {
            weekdays.forEach(key => {
                daysConfigFields.push({
                    day: key,
                    label: <Localized {...weekdayText(WeekDay[key])} />,
                    values: watch(`${FormFields.config}.${key}`),
                });
            });
        }
        if (allWeekendSame) {
            daysConfigFields.push({
                day: 'weekend',
                label: (
                    <Localized
                        de="Samstag - Sonntag"
                        fr="Samedi - Dimanche"
                        it="Sabato - Domenica"
                        en="Saturday - Sunday"
                    />
                ),
                values: saturday,
            });
        } else {
            weekend.forEach(key => {
                daysConfigFields.push({
                    day: key,
                    label: <Localized {...weekdayText(WeekDay[key])} />,
                    values: watch(`${FormFields.config}.${key}`),
                });
            });
        }

        return daysConfigFields;
    }, [allDaysSame, allWeekendSame, allWeekdaysSame, monday, saturday, watch]);

    return (
        <Box
            sx={{
                display: 'flex',
                height: '100%',
                color: theme => theme.palette.blue.main,
                padding: '20px 24px',
                ...Typo.robotoMedium,
            }}
        >
            {openDailyConfigEditModal !== null && (
                <DailyConfigEditModal
                    dailyConfigField={openDailyConfigEditModal}
                    onClose={() => setOpenDailyConfigEditModal(null)}
                    profiles={profiles}
                    watch={watch}
                    setValue={setValue}
                />
            )}
            <FormContentLayout>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            width: '100%',
                            marginBottom: '10px',
                        }}
                    >
                        <FormControlLabel
                            label={
                                <Box sx={{ ...Typo.robotoMedium }}>
                                    <Localized
                                        de="Jeder Tag gleich"
                                        fr="Chaque jour est pareil"
                                        it="Tutti i giorni uguali"
                                        en="All days same"
                                    />
                                </Box>
                            }
                            control={
                                <Switch
                                    checked={allDaysSame}
                                    onChange={(_e, checked) => {
                                        setAllDaysSame(checked);
                                        onWeeklyConfigChange(
                                            config,
                                            (
                                                newConfig: SmartCallWeeklyConfig,
                                            ) =>
                                                setValue(
                                                    FormFields.config,
                                                    newConfig,
                                                    {
                                                        shouldDirty: true,
                                                        shouldTouch: true,
                                                        shouldValidate: true,
                                                    },
                                                ),
                                            monday,
                                            'allDays',
                                        );
                                    }}
                                />
                            }
                        />
                    </Box>

                    {allDaysSame ? (
                        <Box
                            sx={{
                                display: 'flex',
                                height: '100%',
                            }}
                        >
                            <FormLeftColumn>
                                {dailyConfigurationFields.map(
                                    dailyConfigField => (
                                        <DailyConfigFieldSegment
                                            key={dailyConfigField.day}
                                            label={dailyConfigField.label}
                                            timeWindowConfigs={
                                                dailyConfigField.values
                                            }
                                            profiles={profiles}
                                            onEdit={() =>
                                                setOpenDailyConfigEditModal(
                                                    dailyConfigField,
                                                )
                                            }
                                        />
                                    ),
                                )}
                            </FormLeftColumn>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', height: '100%' }}>
                            <FormLeftColumn>
                                <FormControlLabel
                                    label={
                                        <Box sx={{ ...Typo.robotoMedium }}>
                                            <Localized
                                                de="Mon - Fre gleich"
                                                fr="Lun - Ven même"
                                                it="Lun - Ven uguale"
                                                en="Mon - Fre same"
                                            />
                                        </Box>
                                    }
                                    control={
                                        <Switch
                                            checked={allWeekdaysSame}
                                            onChange={(_e, checked) => {
                                                setAllWeekdaysSame(checked);
                                                onWeeklyConfigChange(
                                                    config,
                                                    (
                                                        newConfig: SmartCallWeeklyConfig,
                                                    ) =>
                                                        setValue(
                                                            FormFields.config,
                                                            newConfig,
                                                            {
                                                                shouldDirty: true,
                                                                shouldTouch: true,
                                                                shouldValidate: true,
                                                            },
                                                        ),
                                                    monday,
                                                    'weekdays',
                                                );
                                            }}
                                        />
                                    }
                                />

                                {dailyConfigurationFields
                                    .filter(
                                        dailyConfigField =>
                                            dailyConfigField.day ===
                                                'weekdays' ||
                                            isWeekday(dailyConfigField.day),
                                    )
                                    .map(dailyConfigField => (
                                        <DailyConfigFieldSegment
                                            key={dailyConfigField.day}
                                            label={dailyConfigField.label}
                                            timeWindowConfigs={
                                                dailyConfigField.values
                                            }
                                            profiles={profiles}
                                            onEdit={() =>
                                                setOpenDailyConfigEditModal(
                                                    dailyConfigField,
                                                )
                                            }
                                        />
                                    ))}
                            </FormLeftColumn>

                            <FormRightColumn>
                                <FormControlLabel
                                    label={
                                        <Box sx={{ ...Typo.robotoMedium }}>
                                            <Localized
                                                de="Sam - Son gleich"
                                                fr="Sam - Dim même"
                                                it="Sab - Dom uguale"
                                                en="Sat - Sun same"
                                            />
                                        </Box>
                                    }
                                    control={
                                        <Switch
                                            checked={allWeekendSame}
                                            onChange={(_e, checked) => {
                                                setAllWeekendSame(checked);
                                                onWeeklyConfigChange(
                                                    config,
                                                    (
                                                        newConfig: SmartCallWeeklyConfig,
                                                    ) =>
                                                        setValue(
                                                            FormFields.config,
                                                            newConfig,
                                                            {
                                                                shouldDirty: true,
                                                                shouldTouch: true,
                                                                shouldValidate: true,
                                                            },
                                                        ),
                                                    saturday,
                                                    'weekend',
                                                );
                                            }}
                                        />
                                    }
                                />
                                {dailyConfigurationFields
                                    .filter(
                                        dailyConfigField =>
                                            dailyConfigField.day ===
                                                'weekend' ||
                                            isWeekend(dailyConfigField.day),
                                    )
                                    .map(dailyConfigField => (
                                        <DailyConfigFieldSegment
                                            key={dailyConfigField.day}
                                            label={dailyConfigField.label}
                                            timeWindowConfigs={
                                                dailyConfigField.values
                                            }
                                            profiles={profiles}
                                            onEdit={() =>
                                                setOpenDailyConfigEditModal(
                                                    dailyConfigField,
                                                )
                                            }
                                        />
                                    ))}
                            </FormRightColumn>
                        </Box>
                    )}
                </Box>
            </FormContentLayout>
        </Box>
    );
}

export function DailyConfigEditModal({
    dailyConfigField,
    onClose,
    profiles,
    watch,
    setValue,
}: {
    dailyConfigField: DailyConfigField;
    onClose: () => void;
    profiles: SmartCallProfile[];
    watch: UseFormWatch<EditPayload>;
    setValue: UseFormSetValue<EditPayload>;
}) {
    const groupDayMappings: Record<
        'allDays' | 'weekdays' | 'weekend',
        WeeklyConfigFields[]
    > = {
        allDays: [
            WeeklyConfigFields.monday,
            WeeklyConfigFields.tuesday,
            WeeklyConfigFields.wednesday,
            WeeklyConfigFields.thursday,
            WeeklyConfigFields.friday,
            WeeklyConfigFields.saturday,
            WeeklyConfigFields.sunday,
        ],
        weekdays: [
            WeeklyConfigFields.monday,
            WeeklyConfigFields.tuesday,
            WeeklyConfigFields.wednesday,
            WeeklyConfigFields.thursday,
            WeeklyConfigFields.friday,
        ],
        weekend: [WeeklyConfigFields.saturday, WeeklyConfigFields.sunday],
    };

    function isGroupedDay(
        day: DailyConfigField['day'],
    ): day is keyof typeof groupDayMappings {
        return day === 'weekdays' || day === 'weekend' || day === 'allDays';
    }

    const dayKeys: WeeklyConfigFields[] = isGroupedDay(dailyConfigField.day)
        ? groupDayMappings[dailyConfigField.day]
        : [dailyConfigField.day];

    const mainDayKey: WeeklyConfigFields = dayKeys[0];
    const timeWindowConfigs: SmartCallTimeWindowConfig[] = watch(
        `${FormFields.config}.${mainDayKey}`,
    );

    return (
        <SmartCallTimeWindowConfigEditDialog
            title={dailyConfigField.label}
            profiles={profiles}
            timeWindowConfigs={timeWindowConfigs}
            onConfirm={timeWindowConfigs => {
                if (isGroupedDay(dailyConfigField.day)) {
                    dayKeys.forEach((day: WeeklyConfigFields) =>
                        setValue(
                            `${FormFields.config}.${day}`,
                            timeWindowConfigs as never, // can't infer nested array type
                            {
                                shouldDirty: true,
                                shouldTouch: true,
                                shouldValidate: true,
                            },
                        ),
                    );
                } else {
                    setValue(
                        `${FormFields.config}.${dailyConfigField.day}`,
                        timeWindowConfigs as never, // can't infer nested array type
                        {
                            shouldDirty: true,
                            shouldTouch: true,
                            shouldValidate: true,
                        },
                    );
                }

                onClose();
            }}
            onCancel={onClose}
        />
    );
}

function DailyConfigFieldSegment({
    label,
    timeWindowConfigs,
    profiles,
    onEdit,
}: {
    label: JSX.Element;
    timeWindowConfigs: SmartCallTimeWindowConfig[];
    profiles: SmartCallProfile[];
    onEdit: () => void;
}) {
    const hasConfig = timeWindowConfigs.length > 0;
    const defaultProfile = profiles.find(profile => profile.isDefault);
    const fullDayConfigured = hasFullDayConfigured(timeWindowConfigs);

    if (!defaultProfile) {
        return (
            <Alert severity="error">
                <Localized
                    de="Das Standard-Profil fehlt"
                    fr="Le profil par défaut est manquant"
                    it="Il profilo predefinito è mancante"
                    en="The default profile is missing"
                />
            </Alert>
        );
    }

    return (
        <Box>
            <DailyConfigHeader title={label} onEdit={onEdit} />
            <Box sx={{ margin: '24px' }}>
                {hasConfig ? (
                    <Box>
                        {timeWindowConfigs
                            .filter(
                                config =>
                                    config.smartCallProfileId &&
                                    config.start &&
                                    config.end,
                            )
                            .map(timeWindowConfig => (
                                <SmartCallTimeWindowConfigDisplay
                                    key={timeWindowConfig.start}
                                    timeWindow={timeWindowConfig}
                                    profiles={profiles}
                                />
                            ))}
                        {!fullDayConfigured && (
                            <Box sx={{ marginTop: '10px' }}>
                                <Localized
                                    de={`Ausserhalb: ${defaultProfile.description}`}
                                    fr={`En dehors: ${defaultProfile.description}`}
                                    it={`Al di fuori: ${defaultProfile.description}`}
                                    en={`Outside: ${defaultProfile.description}`}
                                />
                            </Box>
                        )}
                    </Box>
                ) : (
                    <>{defaultProfile.description}</>
                )}
            </Box>
        </Box>
    );
}

export function DailyConfigHeader({
    title,
    onEdit,
}: {
    title: JSX.Element;
    onEdit: () => void;
}) {
    return (
        <Box
            sx={{
                ...OperatorTypo.headingOne,
                textTransform: 'uppercase',
                fontWeight: '600',
                color: theme => theme.palette.blue.main,
                backgroundColor: theme => theme.palette.grey.A200,
                display: 'flex',
                justifyContent: 'space-between',
            }}
        >
            <Box sx={{ margin: '2px 8px' }}>{title}</Box>

            <Clickable
                element={'div'}
                onClick={onEdit}
                className={css({ display: 'flex', margin: '2px' })}
            >
                <Edit />
            </Clickable>
        </Box>
    );
}

const weekdays = [
    WeeklyConfigFields.monday,
    WeeklyConfigFields.tuesday,
    WeeklyConfigFields.wednesday,
    WeeklyConfigFields.thursday,
    WeeklyConfigFields.friday,
] as Array<WeeklyConfigFields>;

const weekend = [
    WeeklyConfigFields.saturday,
    WeeklyConfigFields.sunday,
] as Array<WeeklyConfigFields>;

function isWeekday(day: string): day is WeeklyConfigFields {
    return weekdays.includes(day as WeeklyConfigFields);
}

function isWeekend(day: string): day is WeeklyConfigFields {
    return weekend.includes(day as WeeklyConfigFields);
}

interface DailyConfigField {
    label: JSX.Element;
    day: WeeklyConfigDayKeys;
    values: SmartCallTimeWindowConfig[];
}

type WeeklyConfigDayKeys =
    | WeeklyConfigFields
    | 'weekdays'
    | 'weekend'
    | 'allDays';

function onWeeklyConfigChange(
    currentConfig: SmartCallWeeklyConfig,
    setWeeklyConfig: (newConfig: SmartCallWeeklyConfig) => void,
    timeWindowConfigs: SmartCallTimeWindowConfig[],
    dayKey: WeeklyConfigDayKeys,
) {
    let weeklyConfig: SmartCallWeeklyConfig;

    if (dayKey === 'weekdays') {
        weeklyConfig = {
            ...currentConfig,
            monday: timeWindowConfigs,
            tuesday: timeWindowConfigs,
            wednesday: timeWindowConfigs,
            thursday: timeWindowConfigs,
            friday: timeWindowConfigs,
        };
    } else if (dayKey === 'weekend') {
        weeklyConfig = {
            ...currentConfig,
            saturday: timeWindowConfigs,
            sunday: timeWindowConfigs,
        };
    } else if (dayKey === 'allDays') {
        weeklyConfig = {
            monday: timeWindowConfigs,
            tuesday: timeWindowConfigs,
            wednesday: timeWindowConfigs,
            thursday: timeWindowConfigs,
            friday: timeWindowConfigs,
            saturday: timeWindowConfigs,
            sunday: timeWindowConfigs,
        };
    } else {
        weeklyConfig = {
            ...currentConfig,
            [dayKey]: timeWindowConfigs,
        };
    }

    setWeeklyConfig(weeklyConfig);
}

function areAllDaysEqual(days: SmartCallTimeWindowConfig[][]): boolean {
    if (days.length <= 1) return true;

    const [first, ...rest] = days;
    return rest.every(day => areDailyConfigsEqual(day, first));
}

function areDailyConfigsEqual(
    currentDay: SmartCallTimeWindowConfig[],
    firstDay: SmartCallTimeWindowConfig[],
) {
    if (currentDay.length !== firstDay.length) return false;
    return currentDay.every((currentDayTimeWindowConfig, index) => {
        const firstDayTimeWindowConfig = firstDay[index];
        return (
            currentDayTimeWindowConfig.smartCallProfileId ===
                firstDayTimeWindowConfig.smartCallProfileId &&
            currentDayTimeWindowConfig.start ===
                firstDayTimeWindowConfig.start &&
            currentDayTimeWindowConfig.end === firstDayTimeWindowConfig.end
        );
    });
}
